import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProvince } from '../hooks/useProvince';
import { useTargetIntel } from '../hooks/useTargetIntel';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { useWarRoomState } from '../hooks/useWarRoomState';
import { Spinner } from '../components/Spinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { AttackResult as AttackResultComponent } from '../components/AttackResult';
import { TargetSelector } from '../components/attack/TargetSelector';
import { QuickIntelFeedback } from '../components/attack/QuickIntelFeedback';
import { AttackTypeSelector } from '../components/attack/AttackTypeSelector';
import { MobileWarRoomShell } from '../components/attack/MobileWarRoomShell';
import { DesktopWarRoomShell } from '../components/attack/DesktopWarRoomShell';
import { AmbushLaunchPanel } from '../components/attack/AmbushLaunchPanel';
import { fetchAmbushableAttacks } from '../api/attack';
import { getKingdom } from '../api/magic';
import { castSpell } from '../api/magic';
import { conductThieveryOperation } from '../api/thievery';
import { COLORS } from '../constants/theme';
import type { Province } from '../types/province';
import type { AttackType, AttackResult } from '../types/attack';
import type { Kingdom, KingdomMember } from '../types/magic';
import type { QuickIntelFeedback as QuickIntelFeedbackType } from '../types/attackCalculator';

type AttackMode = 'attack' | 'ambush';

/**
 * Loading/error gate only — `province` becomes a guaranteed non-null value below this point, so
 * `AttackContent` (and the `useWarRoomState()` call it owns) only ever mounts once real data is
 * available. `province` doesn't go null again after a successful load (`refresh()` updates it in
 * place), so this split doesn't cause `AttackContent` to remount/reset state on every refetch.
 */
export function Attack() {
  const { provinceId, worldId } = useAuth();
  const { province, isLoading, error, refresh } = useProvince();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-4">
        <ErrorMessage message={error} />
      </div>
    );
  }

  if (!province) {
    return (
      <div className="container mx-auto px-4 py-4">
        <ErrorMessage message="Province data not available" />
      </div>
    );
  }

  return (
    <AttackContent
      province={province}
      provinceId={provinceId}
      worldId={worldId}
      refresh={refresh}
    />
  );
}

interface AttackContentProps {
  province: Province;
  provinceId: string | null;
  worldId: string | null;
  refresh: () => Promise<void>;
}

function AttackContent({ province, provinceId, worldId, refresh }: AttackContentProps) {
  const platform = useAppShellPlatform();
  const [searchParams] = useSearchParams();

  // SF-147 Component 8: Ambush's entry point is a dedicated page-level mode toggle, not a value
  // of selectedAttackType — this makes selectedAttackType === 'AMBUSH' permanently unreachable,
  // which in turn makes the old dropdown-driven Ambush-reset effects fully obsolete rather than
  // needing relocation (Component 9).
  const [attackMode, setAttackMode] = useState<AttackMode>('attack');

  // --- Target Selection ---
  const [kingdomNumber, setKingdomNumber] = useState('');
  const [islandNumber, setIslandNumber] = useState('');
  const [selectedTarget, setSelectedTarget] = useState('');
  const [kingdomMembers, setKingdomMembers] = useState<KingdomMember[]>([]);
  const [isLoadingKingdom, setIsLoadingKingdom] = useState(false);
  // SF-174: caches the full fetched Kingdom object (not just .members) so a cache hit still
  // carries stance/dragon for estimatedGain.ts's gainMod — a members-only cache would leave
  // ownKingdomInfo/targetKingdomInfo stale on any repeat "Load Kingdom" of an already-cached id.
  const [kingdomCache, setKingdomCache] = useState<Record<string, Kingdom>>({});
  const [hasAutoLoadedKingdom, setHasAutoLoadedKingdom] = useState(false);
  const [ownKingdomInfo, setOwnKingdomInfo] = useState<Kingdom | null>(null);
  const [targetKingdomInfo, setTargetKingdomInfo] = useState<Kingdom | null>(null);

  const [selectedAttackType, setSelectedAttackType] = useState<AttackType>('TRADITIONAL_MARCH');

  // --- Intel ---
  const intel = useTargetIntel(selectedTarget || null);

  // --- Quick Intel ---
  const [isQuickIntelLoading, setIsQuickIntelLoading] = useState<string | null>(null);
  const [quickIntelFeedback, setQuickIntelFeedback] = useState<QuickIntelFeedbackType | null>(null);

  // --- Attack Result (shared by both War Room and Ambush flows) ---
  const [attackResult, setAttackResult] = useState<AttackResult | null>(null);

  // --- Ambush opportunity count badge (Component 8) ---
  const [ambushCount, setAmbushCount] = useState(0);
  // SF-96: bumped by AmbushLaunchPanel's own submit flow after an AMBUSH_NOT_ELIGIBLE
  // response — the async write queues mean a just-consumed source can transiently reappear, so
  // treat that as "list went stale" and refetch both the panel's row list and this page-level
  // badge count.
  const [ambushRefreshTrigger, setAmbushRefreshTrigger] = useState(0);

  useEffect(() => {
    if (!worldId || !provinceId) return;
    let cancelled = false;
    fetchAmbushableAttacks(worldId, provinceId)
      .then((attacks) => {
        if (!cancelled) setAmbushCount(attacks.length);
      })
      .catch(() => {
        // Badge count is best-effort — a failed fetch just leaves the prior count displayed.
      });
    return () => {
      cancelled = true;
    };
  }, [worldId, provinceId, ambushRefreshTrigger]);

  // --- Auto-load own kingdom ---
  useEffect(() => {
    async function autoLoadOwnKingdom() {
      if (hasAutoLoadedKingdom || !provinceId || !worldId) return;

      const { island, kingdom } = province.metadata.location;
      const kingdomId = `${kingdom}:${island}`;

      setIslandNumber(String(island));
      setKingdomNumber(String(kingdom));
      setHasAutoLoadedKingdom(true);

      if (kingdomCache[kingdomId]) {
        setKingdomMembers(kingdomCache[kingdomId].members.filter((p) => p.provinceId !== provinceId));
        setOwnKingdomInfo(kingdomCache[kingdomId]);
        return;
      }

      setIsLoadingKingdom(true);
      try {
        const kingdomData: Kingdom = await getKingdom(worldId, kingdomId);
        const members = kingdomData.members.filter(
          (p: KingdomMember) => p.provinceId !== provinceId
        );
        setKingdomMembers(members);
        setOwnKingdomInfo(kingdomData);
        setKingdomCache((prev) => ({ ...prev, [kingdomId]: kingdomData }));
      } catch {
        // Silently fail
      } finally {
        setIsLoadingKingdom(false);
      }
    }

    autoLoadOwnKingdom();
  }, [province, hasAutoLoadedKingdom, kingdomCache, provinceId, worldId]);

  // --- Pre-select target from query params ---
  useEffect(() => {
    const targetId = searchParams.get('targetId');
    const targetLoc = searchParams.get('targetLoc');
    if (targetId && targetLoc) {
      const parts = targetLoc.split(':');
      if (parts.length === 2) {
        setKingdomNumber(parts[0]);
        setIslandNumber(parts[1]);
      }
      setSelectedTarget(targetId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setAttackResult(null);
  }, [selectedTarget]);

  // --- Handlers ---

  const handleChangeKingdom = async () => {
    setAttackResult(null);

    if (!islandNumber || !kingdomNumber || !worldId) {
      return;
    }

    const kingdomId = `${kingdomNumber}:${islandNumber}`;

    if (kingdomCache[kingdomId]) {
      setKingdomMembers(kingdomCache[kingdomId].members.filter((p) => p.provinceId !== provinceId));
      setTargetKingdomInfo(kingdomCache[kingdomId]);
      setSelectedTarget('');
      return;
    }

    setIsLoadingKingdom(true);
    try {
      const kingdom: Kingdom = await getKingdom(worldId, kingdomId);
      const members = kingdom.members.filter((p: KingdomMember) => p.provinceId !== provinceId);
      setKingdomMembers(members);
      setTargetKingdomInfo(kingdom);
      setKingdomCache({ ...kingdomCache, [kingdomId]: kingdom });
      setSelectedTarget('');
    } catch {
      setKingdomMembers([]);
    } finally {
      setIsLoadingKingdom(false);
    }
  };

  const handleTargetChange = (targetId: string) => {
    setSelectedTarget(targetId);
    setAttackResult(null);
  };

  const handleQuickIntel = useCallback(async (type: 'CB' | 'SOM' | 'SURVEY' | 'SOS' | 'SOHD' | 'SOT') => {
    if (!provinceId || !worldId || !selectedTarget) return;

    setIsQuickIntelLoading(type);
    setQuickIntelFeedback(null);

    try {
      if (type === 'CB') {
        const result = await castSpell(worldId, provinceId, {
          spellType: 'CRYSTAL_BALL',
          targetProvinceId: selectedTarget,
        });
        setQuickIntelFeedback({
          type: result.success ? 'success' : 'error',
          message: result.message,
          timestamp: Date.now(),
        });
      } else {
        const operationMap = {
          SOM: 'SPY_ON_MILITARY' as const,
          SURVEY: 'SURVEY' as const,
          SOS: 'SPY_ON_SCIENCE' as const,
          SOHD: 'SPY_ON_HOME_DEFENSE' as const,
          SOT: 'SPY_ON_THRONE' as const,
        };
        const result = await conductThieveryOperation(worldId, provinceId, {
          targetProvinceId: selectedTarget,
          operationName: operationMap[type],
          damageTier: null,
        });
        setQuickIntelFeedback({
          type: result.result.success ? 'success' : 'error',
          message: result.result.message,
          timestamp: Date.now(),
        });
      }

      await intel.refreshIntel();
      await refresh();
    } catch (err) {
      setQuickIntelFeedback({
        type: 'error',
        message: err instanceof Error ? err.message : 'Operation failed',
        timestamp: Date.now(),
      });
    } finally {
      setIsQuickIntelLoading(null);
    }
  }, [provinceId, worldId, selectedTarget, intel, refresh]);

  const targetMember = kingdomMembers.find((m) => m.provinceId === selectedTarget) ?? null;

  const handleRunSohd = useCallback(() => {
    void handleQuickIntel('SOHD');
  }, [handleQuickIntel]);

  // SF-147 Component 1, Phase 3 Decision 7: called exactly once here (not per-shell) — a viewport
  // breakpoint flip would otherwise unmount one shell and mount the other, re-running this hook
  // from scratch and silently discarding the player's in-progress army composition.
  const warRoomState = useWarRoomState({
    province,
    intel,
    targetMember,
    islandNumber,
    kingdomNumber,
    selectedAttackType,
    onAttackTypeChange: setSelectedAttackType,
    onRunSohd: handleRunSohd,
    worldId,
    provinceId,
    refresh,
    onAttackComplete: setAttackResult,
    ownKingdomInfo,
    targetKingdomInfo,
  });

  const handleAmbushRefreshTriggerBump = useCallback(() => {
    setAmbushRefreshTrigger((n) => n + 1);
  }, []);

  // SF-174 Component 1: the "War Room" <h1> moves up from the shells to page level, above the
  // Attack/Ambush mode toggle — renders in both modes now (previously Ambush-absent only
  // because it lived inside each shell, which only mounts in attack mode). Keeps the shells'
  // existing route-change focus effect verbatim.
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  // SF-149 post-launch-feedback convention: imperative focus move to the result on the
  // null→set transition.
  const attackResultRef = useRef<HTMLDivElement>(null);
  const prevAttackResultRef = useRef<AttackResult | null>(null);
  useEffect(() => {
    if (attackResult && !prevAttackResultRef.current) {
      attackResultRef.current?.focus();
    }
    prevAttackResultRef.current = attackResult;
  }, [attackResult]);

  return (
    <div className="space-y-4">
      <h1 ref={h1Ref} tabIndex={-1} className="text-lg font-bold outline-none" style={{ color: COLORS.text.primary }}>
        War Room
      </h1>

      {/* War phase banner — shown when the player's kingdom is at war */}
      {province.kingdom.isAtWar && (
        <div
          style={{
            padding: '8px 12px',
            backgroundColor: '#2a1414',
            border: '1px solid #7a2020',
            borderRadius: 6,
            fontSize: 12,
            color: COLORS.text.secondary,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span>
            <span style={{ color: '#c45a4a', fontWeight: 700, marginRight: 6 }}>⚔ At War</span>
            Mana &amp; stealth costs reduced to 2% · Attack time reduced · Explore costs doubled
          </span>
          <a
            href="/relations"
            style={{ color: COLORS.link, fontSize: 11, whiteSpace: 'nowrap' }}
          >
            View Relations →
          </a>
        </div>
      )}

      {/* Attack Result */}
      {attackResult && (
        <div ref={attackResultRef} tabIndex={-1} className="outline-none">
          <AttackResultComponent
            result={attackResult}
            currentTick={province.sync.currentTick}
            tickIntervalMs={province.sync.tickIntervalMs}
          />
        </div>
      )}

      {/* Attack/Ambush mode toggle — SF-147 Component 8: replaces the dropdown-based entry point,
          with a live opportunity-count badge instead of a hidden mode switch. */}
      <div role="radiogroup" aria-label="Attack mode" className="flex gap-1.5">
        <button
          type="button"
          role="radio"
          aria-checked={attackMode === 'attack'}
          onClick={() => setAttackMode('attack')}
          className="flex-1 rounded text-sm font-semibold px-3"
          style={{
            minHeight: 44,
            border: `1px solid ${attackMode === 'attack' ? COLORS.link : COLORS.border}`,
            backgroundColor: attackMode === 'attack' ? 'rgba(106, 158, 142, 0.15)' : COLORS.sectionHeader,
            color: attackMode === 'attack' ? COLORS.link : COLORS.text.secondary,
          }}
        >
          Attack
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={attackMode === 'ambush'}
          onClick={() => setAttackMode('ambush')}
          className="flex-1 rounded text-sm font-semibold px-3"
          style={{
            minHeight: 44,
            border: `1px solid ${attackMode === 'ambush' ? COLORS.link : COLORS.border}`,
            backgroundColor: attackMode === 'ambush' ? 'rgba(106, 158, 142, 0.15)' : COLORS.sectionHeader,
            color: attackMode === 'ambush' ? COLORS.link : COLORS.text.secondary,
          }}
        >
          Ambush{ambushCount > 0 ? ` (${ambushCount})` : ''}
        </button>
      </div>

      {attackMode === 'attack' && (
        <>
          <TargetSelector
            kingdomNumber={kingdomNumber}
            islandNumber={islandNumber}
            selectedTarget={selectedTarget}
            kingdomMembers={kingdomMembers}
            isLoadingKingdom={isLoadingKingdom}
            isQuickIntelLoading={isQuickIntelLoading}
            hasTarget={!!selectedTarget}
            currentTick={province.sync.currentTick}
            onKingdomChange={setKingdomNumber}
            onIslandChange={setIslandNumber}
            onLoadKingdom={handleChangeKingdom}
            onTargetChange={handleTargetChange}
            onQuickIntel={handleQuickIntel}
          />

          {quickIntelFeedback && (
            <QuickIntelFeedback feedback={quickIntelFeedback} />
          )}

          <AttackTypeSelector
            selectedAttackType={selectedAttackType}
            onAttackTypeChange={setSelectedAttackType}
          />

          {platform === 'mobile' ? (
            <MobileWarRoomShell {...warRoomState} />
          ) : (
            <DesktopWarRoomShell {...warRoomState} />
          )}
        </>
      )}

      {attackMode === 'ambush' && (
        <AmbushLaunchPanel
          province={province}
          worldId={worldId ?? ''}
          provinceId={provinceId ?? ''}
          refreshTrigger={ambushRefreshTrigger}
          onRefreshTriggerBump={handleAmbushRefreshTriggerBump}
          onAttackComplete={setAttackResult}
          refresh={refresh}
        />
      )}
    </div>
  );
}
